// src/pages/Calibration/CalibrationUnitForm.jsx
//
// W-30 (2026-10-07): add a calibration instrument unit (a physical gage) to the register.
// Before this, "+ Add Instrument Unit" went to /calibration/units/new, which opened the unit detail
// page with id "new": the web could not add units at all.
//
// Fields are exactly what POST /api/calibration/units accepts (production route
// src/routes/calibrationInstrumentUnits.js): instrument_id, serial_number, asset_tag, make, model,
// frequency_days_override, notes. The server sets status ("unverified") itself; calibration dates
// come from recording a calibration event afterwards. Labels follow the iPad's New Asset form where
// the field is the same (Asset ID = asset_tag, Manufacturer = make, Model, Serial #, Notes).
//
// Needs manage_calibration (S-71: Admin, Quality Manager, Quality Control Inspector). The server
// refuses the write without it (403); this page says so instead of showing a form.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Stack, Typography, Breadcrumbs, Box, Button, Alert, Grid,
  TextField, MenuItem, CircularProgress, IconButton,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { ArrowBackRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";

import { calibrationInstrumentsApi, calibrationUnitsApi, CATEGORY_LABELS } from "../../utils/calibrationApi";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, FieldLabel,
} from "../../components/PaperForm";

// Column sizes on calibration_instrument_units (the server would reject longer values).
const MAX = { serial_number: 120, asset_tag: 60, make: 120, model: 120 };

// The server's own words: { error } from the route, { message } from the permission check.
const serverMessage = (err) => {
  const d = err?.response?.data;
  if (d && typeof d === "object") return d.error || d.message || err.message;
  if (typeof d === "string" && d.trim()) return d;
  return err?.message || "Request failed";
};

export default function CalibrationUnitForm() {
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);
  const canManageCalibration = hasPermission(loginUser?.decodedToken?.permissions || [], PERMISSIONS.MANAGE_CALIBRATION);

  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [instrumentId, setInstrumentId] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [assetTag, setAssetTag] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [frequencyOverride, setFrequencyOverride] = useState("");
  const [notes, setNotes] = useState("");

  // False once the user has left this page: a slow save that succeeds afterwards still reports it,
  // but does not pull the user to the new unit from wherever they went.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!canManageCalibration) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const res = await calibrationInstrumentsApi.list();
        if (alive) setInstruments(res?.data?.data || []);
      } catch (err) {
        if (alive) setLoadError(serverMessage(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [canManageCalibration]);

  const selected = instruments.find((i) => String(i.id) === String(instrumentId)) || null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;
    setError(null);
    if (!instrumentId) { setError("Instrument type is required."); return; }
    if (!serialNumber.trim()) { setError("Serial # is required."); return; }
    let frequency = null;
    if (String(frequencyOverride).trim() !== "") {
      frequency = Number(frequencyOverride);
      if (!Number.isInteger(frequency) || frequency <= 0) {
        setError("Calibration cycle override must be a whole number of days greater than 0, or left blank.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        instrument_id: Number(instrumentId),
        serial_number: serialNumber.trim(),
        asset_tag: assetTag.trim() || null,
        make: make.trim() || null,
        model: model.trim() || null,
        frequency_days_override: frequency,
        notes: notes.trim() || null,
      };
      const res = await calibrationUnitsApi.create(payload);
      const newId = res?.data?.data?.id;
      if (!(res?.status >= 200 && res?.status < 300) || !newId) {
        const msg = `The server did not confirm the unit was saved (status ${res?.status ?? "unknown"}).`;
        if (mounted.current) setError(msg);
        else toast.error(`Instrument unit ${payload.serial_number}: ${msg}`);
        return;
      }
      toast.success(`Instrument unit ${payload.serial_number} added`);
      if (mounted.current) navigate(`/calibration/units/${newId}`);
    } catch (err) {
      const msg = serverMessage(err);
      if (mounted.current) setError(msg);
      toast.error(msg);
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  const back = (
    <IconButton onClick={() => navigate("/calibration")} aria-label="Back">
      <ArrowBackRounded />
    </IconButton>
  );

  if (!canManageCalibration) {
    return (
      <Box sx={{ p: 3, maxWidth: "md", mx: "auto" }}>
        <Helmet><title>Add Instrument Unit | SFM</title></Helmet>
        <Alert severity="warning">You do not have permission to add calibration instrument units.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/calibration")}>Back to the calibration register</Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ pt: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet><title>Add Instrument Unit | SFM</title></Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <RouterLink to="/dashboard/app" style={{ color: "inherit", textDecoration: "none" }}>Home</RouterLink>
              <RouterLink to="/calibration" style={{ color: "inherit", textDecoration: "none" }}>Calibration</RouterLink>
              <Typography color="text.primary">New Instrument Unit</Typography>
            </Breadcrumbs>
            {back}
          </Stack>

          {loadError && (
            <Alert severity="error" sx={{ mb: 2 }}>Could not load the instrument catalog: {loadError}</Alert>
          )}
          {!loadError && instruments.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Your instrument catalog is empty. An instrument type must be in the catalog before a unit can be added.
              {" "}<RouterLink to="/calibration/catalog">Open the catalog</RouterLink>
              {" "}(adding types needs the Manage Calibration Catalog permission).
            </Alert>
          )}
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · Calibration Unit"
            title="Add Instrument Unit"
            subtitle="AISC 207-25 §2.7 calibration register"
            status="DRAFT"
            number={serialNumber.trim() || "—"}
            numberLabel="Serial No."
            date={new Date().toLocaleDateString()}
          />

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <PaperFormSection label="Asset identification" bottomPadding={0}>
                  <Box>
                    <FieldLabel required hint="from your instrument catalog" id="cal-unit-instrument-label">Instrument Type</FieldLabel>
                    <TextField
                      select fullWidth size="small"
                      id="cal-unit-instrument"
                      value={instrumentId}
                      onChange={(e) => setInstrumentId(e.target.value)}
                      disabled={instruments.length === 0}
                      SelectProps={{ displayEmpty: true, labelId: "cal-unit-instrument-label" }}
                    >
                      <MenuItem value="" disabled>Select an instrument type</MenuItem>
                      {instruments.map((i) => (
                        <MenuItem key={i.id} value={String(i.id)}>
                          {i.name} · {CATEGORY_LABELS[i.category] || i.category}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box>
                    <FieldLabel required htmlFor="cal-unit-serial">Serial #</FieldLabel>
                    <TextField
                      fullWidth size="small" id="cal-unit-serial"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      inputProps={{ maxLength: MAX.serial_number }}
                    />
                  </Box>
                  <Box>
                    <FieldLabel hint="your shop's asset tag" htmlFor="cal-unit-asset-tag">Asset ID</FieldLabel>
                    <TextField
                      fullWidth size="small" id="cal-unit-asset-tag"
                      value={assetTag}
                      onChange={(e) => setAssetTag(e.target.value)}
                      placeholder="e.g. WG-2026-018"
                      inputProps={{ maxLength: MAX.asset_tag }}
                    />
                  </Box>
                </PaperFormSection>
              </Grid>

              <Grid item xs={12} md={6}>
                <PaperFormSection label="Make & cycle" bottomPadding={0}>
                  <Box>
                    <FieldLabel htmlFor="cal-unit-make">Manufacturer</FieldLabel>
                    <TextField
                      fullWidth size="small" id="cal-unit-make"
                      value={make}
                      onChange={(e) => setMake(e.target.value)}
                      inputProps={{ maxLength: MAX.make }}
                    />
                  </Box>
                  <Box>
                    <FieldLabel htmlFor="cal-unit-model">Model</FieldLabel>
                    <TextField
                      fullWidth size="small" id="cal-unit-model"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      inputProps={{ maxLength: MAX.model }}
                    />
                  </Box>
                  <Box>
                    <FieldLabel hint="leave blank to use the instrument type's cycle" htmlFor="cal-unit-frequency">Calibration Cycle Override (days)</FieldLabel>
                    <TextField
                      fullWidth size="small" type="number" id="cal-unit-frequency"
                      value={frequencyOverride}
                      onChange={(e) => setFrequencyOverride(e.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                      helperText={selected
                        ? `Instrument type cycle: ${selected.default_frequency_days ? `${selected.default_frequency_days} days` : "none (per-use validation)"}`
                        : " "}
                    />
                  </Box>
                </PaperFormSection>
              </Grid>
            </Grid>

            <PaperFormSection label="Notes">
              <TextField
                fullWidth size="small" multiline minRows={2}
                id="cal-unit-notes"
                inputProps={{ "aria-label": "Notes" }}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </PaperFormSection>

            <Alert severity="info" sx={{ mt: 2 }}>
              The unit starts as Unverified. Record its calibration on the unit page to set its status and due date.
            </Alert>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 4 }}>
              <Button
                variant="text"
                onClick={() => navigate("/calibration")}
                disabled={saving}
                sx={{ color: "text.secondary" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || instruments.length === 0}
                sx={{ px: 3, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                {saving ? "Saving…" : "Add Instrument Unit"}
              </Button>
            </Stack>
          </Box>
        </PaperFormCard>
      </Box>
    </>
  );
}
